import React, { useState, useEffect, useRef } from 'react';
import { usePvPGame } from '../hooks/usePvPGame';
import { Play, Clock, AlertCircle, AlertTriangle, CheckCircle, Users } from 'lucide-react';

const PvPGameScreen = ({ userId, onBack }) => {
    const {
        gameState,
        topic,
        myScore,
        opponentScore,
        history,
        messages,
        result,
        timeLeft,
        startMatching,
        submitWord,
        disconnect
    } = usePvPGame(userId);

    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);
    const [count, setCount] = useState(3);

    useEffect(() => {
        if (gameState === 'countdown') {
            setCount(3);
            const timer = setInterval(() => {
                setCount((prev) => (prev > 1 ? prev - 1 : 1));
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [gameState]);

    useEffect(() => {
        // Auto focus input when playing
        if (gameState === 'playing' && inputRef.current) {
            inputRef.current.focus();
        }
    }, [gameState]);

    useEffect(() => {
        // Reset input value when starting matching or countdown (new game)
        if (gameState === 'matching' || gameState === 'countdown') {
            setInputValue('');
        }
    }, [gameState]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const word = inputValue.trim();
        if (!word) return;

        // Simple validation visualization handled by backend response/messages
        submitWord(word);
        setInputValue('');
    };

    // Initial Screen (Matching Lobby)
    if (gameState === 'initial') {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8 animate-fade-in text-center">
                <div className="space-y-2">
                    <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600 drop-shadow-sm">
                        VS MODE
                    </h1>
                    <p className="text-gray-500 font-bold">オンライン対戦 (Beta)</p>
                </div>

                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-100 w-full max-w-xs mx-auto">
                    <p className="text-gray-600 text-sm mb-4">
                        ランダムな相手と30秒間のスコアアタック対決！
                        <br />同じ言葉もOK。スピード勝負！
                    </p>
                    <button
                        onClick={startMatching}
                        className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl text-xl font-black shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
                    >
                        <Users className="w-6 h-6" />
                        MATCH START
                    </button>
                    <button
                        onClick={onBack}
                        className="mt-4 text-gray-400 hover:text-gray-600 text-sm font-bold underline decoration-2 decoration-gray-200 underline-offset-4"
                    >
                        タイトルへ戻る
                    </button>
                </div>
            </div>
        );
    }

    // Matching Screen
    if (gameState === 'matching') {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8 animate-fade-in">
                <div className="relative">
                    <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-200 opacity-75"></div>
                    <div className="relative inline-flex rounded-full h-24 w-24 border-4 border-orange-100 items-center justify-center bg-white shadow-lg">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500"></div>
                    </div>
                </div>

                <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-gray-700">Looking for Opponent...</h2>
                    <p className="text-gray-400 text-sm">対戦相手を探しています</p>
                </div>

                <div className="h-32 w-full max-w-xs bg-gray-50 p-3 rounded-xl border border-gray-100 overflow-y-auto text-xs text-gray-500 space-y-1">
                    {messages.map((m) => (
                        <div key={m.id} className="border-b border-gray-100 last:border-0 pb-1">
                            {m.text}
                        </div>
                    ))}
                </div>

                <button
                    onClick={disconnect}
                    className="px-6 py-2 border-2 border-gray-200 text-gray-500 font-bold rounded-full hover:bg-gray-50 transition-colors"
                >
                    キャンセル
                </button>
            </div>
        );
    }

    // Playing Screen
    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header Area (Topic, Time, Scores) */}
            <div className="bg-gray-100 p-3 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
                {/* VS Badge Background */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-200/50 text-6xl font-black italic pointer-events-none z-0">
                    VS
                </div>

                <div className="relative z-10 flex justify-between items-start">
                    {/* Topic Box */}
                    <div className="text-center">
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">TOPIC</p>
                        <div className="text-4xl font-black text-orange-600 bg-white w-16 h-16 flex items-center justify-center rounded-2xl shadow-sm border-2 border-orange-100">
                            {topic}
                        </div>
                    </div>

                    {/* Right Side: Timer & Scores */}
                    <div className="flex-1 pl-4 flex flex-col gap-3">
                        {/* Timer */}
                        <div className="flex flex-col w-full">
                            <div className="flex items-center justify-end gap-2 mb-1">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className={`text-2xl font-mono font-bold leading-none ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-gray-700'}`}>
                                    {timeLeft}
                                </span>
                                <span className="text-xs text-gray-400">/ 30</span>
                            </div>
                            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-1000 linear ${timeLeft <= 5 ? 'bg-red-500' : 'bg-orange-500'}`}
                                    style={{ width: `${(timeLeft / 30) * 100}%` }}
                                />
                            </div>
                        </div>

                        {/* Scores Comparison */}
                        <div className="flex items-center justify-between bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                            <div className="flex flex-col items-center px-2">
                                <span className="text-[10px] font-bold text-blue-600 uppercase">YOU</span>
                                <span className="text-2xl font-black text-gray-800 tabular-nums transition-all scale-100" key={myScore}>
                                    {myScore}
                                </span>
                            </div>
                            <div className="h-8 w-px bg-gray-200"></div>
                            <div className="flex flex-col items-center px-2">
                                <span className="text-[10px] font-bold text-red-600 uppercase">GUEST</span>
                                <span className="text-2xl font-black text-gray-800 tabular-nums transition-all scale-100" key={opponentScore}>
                                    {opponentScore}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Input Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        disabled={gameState !== 'playing'}
                        placeholder={gameState === 'playing' ? "ひらがなで入力..." : "待機中..."}
                        className="w-full border-2 border-orange-200 rounded-xl p-3 pl-4 text-lg font-bold focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all shadow-sm disabled:opacity-50 disabled:bg-gray-100"
                        autoComplete="off"
                    />
                </div>

                <button
                    type="submit"
                    disabled={gameState !== 'playing' || !inputValue}
                    className="w-full bg-orange-500 text-white rounded-lg py-3 font-bold shadow-md hover:bg-orange-600 active:scale-95 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    <Play className="w-4 h-4 fill-current" />
                    SEND
                </button>
            </form>

            {/* Messages Area (Replaces Toast Overlay for cleaner UI, or keep overlay?)
                User asked to follow other screens. GameScreen has errorMsg below input.
                Let's use a small area below input for latest validation message.
            */}
            <div className="h-6 flex items-center justify-center">
                {messages.length > 0 && (
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-600 animate-fade-in-up">
                        {/* Show only the latest message */}
                        {messages[messages.length - 1].text.includes('無効') ? (
                            <span className="text-red-500 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {messages[messages.length - 1].text}
                            </span>
                        ) : (
                            <span className="text-orange-600 flex items-center gap-1">
                                <AlertCircle className="w-4 h-4" />
                                {messages[messages.length - 1].text}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* History List */}
            <div className="bg-gray-50 rounded-xl p-4 h-[180px] overflow-y-auto border border-gray-200 shadow-inner">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xs text-gray-400 font-bold uppercase tracking-wider">回答履歴</h3>
                    <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">
                        {history.length}
                    </span>
                </div>
                <ul className="flex flex-col gap-2">
                    {/* Reverse history to show newest top? or use flex-col-reverse? GameScreen uses flex-col-reverse. */}
                    {history.map((word, idx) => (
                        <li key={idx} className="bg-white px-3 py-2 rounded-lg text-sm shadow-sm border border-gray-100 flex items-center justify-between animate-slide-in-right">
                            <span className="font-medium text-gray-700">{word}</span>
                            <CheckCircle className="w-4 h-4 text-green-500 opacity-50" />
                        </li>
                    ))}
                    {history.length === 0 && (
                        <li className="text-gray-400 text-sm italic w-full text-center py-8 opacity-50">
                            未回答
                        </li>
                    )}
                </ul>
            </div>

            {/* Countdown Overlay */}
            {gameState === 'countdown' && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
                    <div className="text-8xl font-black text-white drop-shadow-[0_5px_5px_rgba(0,0,0,0.5)] animate-bounce">
                        {count}
                    </div>
                </div>
            )}

            {/* Result Overlay */}
            {gameState === 'finished' && result && (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4 animate-fade-in bg-white/20 backdrop-blur-sm">
                    {/* Card Container */}
                    <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden border-2 border-orange-100 transform scale-100 transition-all">
                        {/* Header Banner */}
                        <div className={`py-6 text-center ${result.result === 'WIN' ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : result.result === 'LOSE' ? 'bg-gray-700' : 'bg-blue-500'}`}>
                            <h2 className="text-4xl font-black text-white italic tracking-wider drop-shadow-md">
                                {result.result === 'WIN' ? 'VICTORY!' : result.result === 'LOSE' ? 'DEFEAT...' : 'DRAW'}
                            </h2>
                        </div>

                        <div className="p-8 space-y-6">
                            {/* Score Comparison Detail */}
                            <div className="flex justify-center items-end gap-6 text-gray-800">
                                <div className="flex flex-col items-center">
                                    <span className="text-xs font-bold text-gray-400 mb-1">YOU</span>
                                    <span className={`text-5xl font-black ${result.result === 'WIN' ? 'text-orange-500' : 'text-gray-700'}`}>
                                        {myScore}
                                    </span>
                                </div>
                                <div className="pb-4 text-gray-300 font-bold text-xl">vs</div>
                                <div className="flex flex-col items-center">
                                    <span className="text-xs font-bold text-gray-400 mb-1">GUEST</span>
                                    <span className={`text-4xl font-bold ${result.result === 'LOSE' ? 'text-gray-900' : 'text-gray-500'}`}>
                                        {opponentScore}
                                    </span>
                                </div>
                            </div>

                            {/* Word History Comparison */}
                            <div className="grid grid-cols-2 gap-2 text-xs h-32 bg-gray-50 rounded-xl p-3 border border-gray-100">
                                <div className="overflow-y-auto pr-1">
                                    <h4 className="font-bold text-blue-500 mb-2 sticky top-0 bg-gray-50 pb-1 border-b border-blue-100">YOU</h4>
                                    <ul className="space-y-1">
                                        {(result.my_history || []).map((w, i) => (
                                            <li key={i} className="text-gray-600 font-medium">{w}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="overflow-y-auto pl-2 border-l border-gray-200">
                                    <h4 className="font-bold text-red-500 mb-2 sticky top-0 bg-gray-50 pb-1 border-b border-red-100">GUEST</h4>
                                    <ul className="space-y-1">
                                        {(result.opponent_history || []).map((w, i) => (
                                            <li key={i} className="text-gray-600 font-medium">{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <button
                                onClick={disconnect}
                                className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-colors shadow-lg flex items-center justify-center gap-2 group"
                            >
                                <Users className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                タイトルへ戻る
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PvPGameScreen;
